import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/novels';

export default function Footer() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://form.readdy.ai/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setSubscribeEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '订阅失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-100">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <h4 className="font-heading text-lg text-background-50 mb-4">嫂子的诱惑</h4>
            <p className="text-sm text-foreground-400 leading-relaxed">
              专注都市禁忌情感小说的内容推荐平台，为言情小说爱好者提供最全面的作品导读与推荐榜单。
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-background-200 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200" aria-label="微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-background-200 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200" aria-label="微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-background-200 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200" aria-label="知乎">
                <i className="ri-zhihu-line"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-base text-background-50 mb-4">分类导航</h4>
            <div className="flex flex-col gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-base text-background-50 mb-4">快速链接</h4>
            <div className="flex flex-col gap-2">
              <Link to="/" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap">首页</Link>
              <Link to="/about" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap">关于我们</Link>
              <a href="#" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap">隐私政策</a>
              <a href="#" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap">用户协议</a>
              <a href="#" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap">版权声明</a>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-base text-background-50 mb-4">订阅更新</h4>
            <p className="text-sm text-foreground-400 mb-3">每周发送最新推荐书单</p>
            {formStatus === 'success' ? (
              <p className="text-sm text-accent-400">感谢订阅！我们会每周发送最新推荐到您的邮箱。</p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col gap-2" data-readdy-form>
                <div className="honeypot-wrapper">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    name="email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    placeholder="输入您的邮箱"
                    required
                    className="flex-1 px-3 py-2 text-sm bg-foreground-800 border border-foreground-700 rounded-md text-background-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="px-4 py-2 text-sm bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap disabled:opacity-50 cursor-pointer"
                  >
                    {formStatus === 'loading' ? '提交中...' : '订阅'}
                  </button>
                </div>
                {formStatus === 'error' && formError && (
                  <p className="text-xs text-red-400">{formError}</p>
                )}
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-foreground-800 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} 嫂子的诱惑编辑部 版权所有
          </p>
          <p className="text-xs text-foreground-500">
            <a href="#" className="hover:text-primary-400 transition-colors" rel="nofollow">京ICP备XXXXXXXX号-1</a>
          </p>
        </div>
      </div>
    </footer>
  );
}